import { useTrans } from '../../../../services/i18n'
import {
  Paper,
  SpacingContainer,
} from '../../../../components/common/Containers'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Grid, Checkbox, CircularProgress, Box } from '@material-ui/core'
import Typography from '../../../../components/common/Typography'
import { Divider } from '../../../../components/common/Divider'
import { Button } from '../../../../components/common/Buttons'
import { useAlertContext } from '../../../../contexts/AlertContext'
import { TabPanel } from '../../../../components/common/Tabs'
import { ReactComponent as IconVLetter } from '../../../../components/assests/ico-V-letter.svg'
import { ReactComponent as IconALetter } from '../../../../components/assests/ico-A-letter.svg'
import OptionsForm from '../optionsForm'
import connect from '../../connect'
import ComponentFeaturesForm from '../ComponentFeaturesForm'
import { selectUserPermissions, isEnabled } from '../../../../selectors'
import { useSelector } from 'react-redux'

/* ------------------------------------------------------------------ */
/* Widget Settings (inline section)                                    */
/* ------------------------------------------------------------------ */

const ALL_WIDGETS = [
  { id: 'calendar', label: 'Calendar' },
  { id: 'SAMStatus', label: 'SAM Status' },
  { id: 'SAPStatus', label: 'SAP Status' },
  { id: 'contracts', label: 'Contracts Won vs Lost' },
  { id: 'vendorManagement', label: 'Vendor Management' },
  { id: 'teamingOpportunities', label: 'Teaming Opportunities' },
  { id: 'exports', label: 'Exports' },
  { id: 'savedSearchs', label: 'Saved Searches at a Glance' },
  { id: 'tasks', label: 'Pipeline/Pursuit Tasks' },
  { id: 'personalTasks', label: 'Personal Tasks' },
  { id: 'recommendedOpportunities', label: 'Recommended Opportunities' },
  { id: 'pursuits', label: 'Pursuits at a Glace' },
]

// which tiers support widget defaults/access and how they map to getFeatures keys
const TIER_FIELD_MAP = {
  advantage: {
    defaultKey: 'defaultAdvantageWidgets',
    accessKey: 'widgetAccessAdvantage',
  },
  pro: {
    defaultKey: 'defaultProWidgets',
    accessKey: 'widgetAccessPro',
  },
  sapAdvantage: {
    defaultKey: 'defaultSapAdvantageWidgets',
    accessKey: 'widgetAccessSapAdvantage',
  },
  procurementPro: {
    defaultKey: 'defaultProcurementProWidgets',
    accessKey: 'widgetAccessProcurementPro',
  },
  appMax: {
    defaultKey: 'defaultAppMaxWidgets',
    accessKey: 'widgetAccessAppMax',
  },
}

/* Renders the two checkbox columns (Default, Access) and wires local state up via callbacks */
function WidgetSettingsSection({
  defaultSelected = [],
  accessSelected = [],
  onChangeDefault,
  onChangeAccess,
}) {
  const { trans } = useTrans()

  const toggle = (arr, id, setter) => {
    const set = new Set(arr)
    if (set.has(id)) set.delete(id)
    else set.add(id)
    setter(Array.from(set))
  }

  const selectAllDefaults = () => onChangeDefault(ALL_WIDGETS.map(w => w.id))
  const clearAllDefaults = () => onChangeDefault([])
  const selectAllAccess = () => onChangeAccess(ALL_WIDGETS.map(w => w.id))
  const clearAllAccess = () => onChangeAccess([])

  return (
    <>
      <Divider variant="middle" width="inherit" light spacing={4} xs={12} />

      <Typography
        variant="h4"
        weight="600"
        fontFamily="lexend"
        fontSize="20px"
        style={{ marginBottom: 12 }}
      >
        {trans('manage-widgets') || 'Manage Widgets'}
      </Typography>

      <Typography variant="body2" style={{ color: '#4E5978', marginBottom: 8 }}>
        {trans('configure-default-and-access-widgets') ||
          'Configure default widgets and which widgets are accessible for this subscription tier'}
      </Typography>

      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 8, marginTop: 8 }}>
        <Grid item xs={12} md={6} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography weight="600">Default</Typography>
          <Button color="primary" variant="text" onClick={selectAllDefaults}>
            {trans('select-all')}
          </Button>
          <Button color="primary" variant="text" onClick={clearAllDefaults}>
            {trans('unselect-all')}
          </Button>
        </Grid>
        <Grid item xs={12} md={6} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography weight="600">Access</Typography>
          <Button color="primary" variant="text" onClick={selectAllAccess}>
            {trans('select-all')}
          </Button>
          <Button color="primary" variant="text" onClick={clearAllAccess}>
            {trans('unselect-all')}
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={1}>
        {ALL_WIDGETS.map(w => (
          <Grid key={w.id} item xs={12}>
            <Grid
              container
              alignItems="center"
              style={{ background: '#F7F8FB', borderRadius: 12, padding: '8px 12px' }}
            >
              <Grid item xs={12} md={6} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Checkbox
                  color="secondary"
                  checked={defaultSelected.includes(w.id)}
                  onChange={() => toggle(defaultSelected, w.id, onChangeDefault)}
                />
                <Typography>{w.label}</Typography>
              </Grid>
              <Grid item xs={12} md={6} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Checkbox
                  color="secondary"
                  checked={accessSelected.includes(w.id)}
                  onChange={() => toggle(accessSelected, w.id, onChangeAccess)}
                />
                <Typography>Can access</Typography>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </>
  )
}

/* ------------------------------------------------------------------ */
/* Main ManageFeatures (Existing Users + Component Features + Widgets) */
/* ------------------------------------------------------------------ */

const ManageFeatures = ({
  optionTab,
  tabTier, // current tier string (e.g. 'advantage', 'pro', etc.)
  updateSubscriptionSettings,
  saveSubscriptionSettings,
  keyTabOptions,
  subscriptionSettings,
  subscriptionSettingsData, // full payload from getFeatures (includes widget arrays)
  updateOrganizationsSettings,
  componentFeatures,
}) => {
  const [isSend, setIsSend] = useState(false)
  const { trans } = useTrans()
  const { successAlert } = useAlertContext()
  const [modules, setModules] = useState([])
  const permissions = useSelector(selectUserPermissions)

  const safeSubscriptionSettings = Array.isArray(subscriptionSettings) ? subscriptionSettings : []

  // determine current tier from props the UI already manages
  const currentTier = useMemo(() => {
    if (tabTier) return tabTier
    const t = keyTabOptions?.[`${optionTab}`]
    return t || null
  }, [tabTier, keyTabOptions, optionTab])

  // local state for widget settings (defaults + access) — initialized from subscriptionSettingsData
  const [defaultWidgets, setDefaultWidgets] = useState([])
  const [widgetAccess, setWidgetAccess] = useState([])

  const mapping = currentTier ? TIER_FIELD_MAP[currentTier] : null
  const supportsWidgets = !!mapping

  useEffect(() => {
    if (!supportsWidgets) {
      setDefaultWidgets([])
      setWidgetAccess([])
      return
    }
    const defaultsArr = subscriptionSettingsData?.[mapping.defaultKey] || []
    const accessArr = subscriptionSettingsData?.[mapping.accessKey] || []
    setDefaultWidgets(Array.isArray(defaultsArr) ? defaultsArr : [])
    setWidgetAccess(Array.isArray(accessArr) ? accessArr : [])
  }, [supportsWidgets, mapping, subscriptionSettingsData])

  const selectUnselectAll = (active, visibilty) => {
    if (!Array.isArray(modules) || modules.length === 0) return
    if (safeSubscriptionSettings.length === 0) return

    let copyModules = modules
    copyModules = copyModules.map(column => {
      const newOptions = column.submodules.map(checkOptions => {
        return {
          ...checkOptions,
          name: checkOptions.name,
          vCheckBox: visibilty ? active : checkOptions.vCheckBox,
          aCheckBox: !visibilty ? active : checkOptions.aCheckBox,
          active: !visibilty ? active : checkOptions.aCheckBox,
        }
      })
      return {
        ...column,
        submodules: newOptions,
      }
    })

    const currentTab = safeSubscriptionSettings.find(obj => obj.idTab === optionTab)
    if (!currentTab) return

    const newTab = {
      ...currentTab,
      existingUsers: {
        optionsList: copyModules,
      },
    }

    const oldTabs = safeSubscriptionSettings.filter(obj => obj.idTab !== optionTab)
    const newTabs = [...oldTabs, newTab]
    updateSubscriptionSettings(newTabs)
  }

  useEffect(() => {
    if (safeSubscriptionSettings.length > 0) {
      const tabUsers = safeSubscriptionSettings.find(obj => obj.idTab === optionTab)
      setModules(tabUsers?.existingUsers?.optionsList || [])
    } else {
      setModules([])
    }
  }, [safeSubscriptionSettings, optionTab])

  /* ------------------------------------------------------------------ */
  /* Page loading state                                                 */
  /* ------------------------------------------------------------------ */

  const isPageLoading = useMemo(() => {
    const hasSettings = safeSubscriptionSettings.length > 0
    const hasComponentFeatures = !!componentFeatures && (!!tabTier ? Array.isArray(componentFeatures?.[tabTier]) : true)
    return !hasSettings || !hasComponentFeatures
  }, [safeSubscriptionSettings, componentFeatures, tabTier])

  /* ------------------------------------------------------------------ */
  /* Dirty-state detection (per section)                                */
  /* ------------------------------------------------------------------ */

  const stableStringify = (value) => {
    if (value === null) return 'null'
    if (value === undefined) return 'undefined'
    if (typeof value !== 'object') return JSON.stringify(value)
    if (value instanceof Date) return JSON.stringify(value.toISOString())
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`

    const keys = Object.keys(value).sort()
    return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`
  }

  const getCurrentOptions = () => {
    if (safeSubscriptionSettings.length === 0) return []
    const currentTabData = safeSubscriptionSettings.find(obj => obj.idTab === optionTab)
    return currentTabData?.existingUsers?.optionsList || []
  }

  const getCurrentComponents = () => componentFeatures?.[tabTier]

  // Store saved baselines as strings to avoid in-place mutation issues from child components
  const savedBaselineRef = useRef({
    optionsStr: '',
    componentsStr: '',
    defaultWidgetsStr: '',
    widgetAccessStr: '',
  })

  const snapshotKeyRef = useRef(null)
  const snapshotKey = `${optionTab}-${currentTier || ''}`

  const currentOptionsStr = useMemo(() => stableStringify(getCurrentOptions()), [safeSubscriptionSettings, optionTab])
  const currentComponentsStr = useMemo(() => stableStringify(getCurrentComponents()), [componentFeatures, tabTier])
  const currentDefaultWidgetsStr = useMemo(() => stableStringify(defaultWidgets), [defaultWidgets])
  const currentWidgetAccessStr = useMemo(() => stableStringify(widgetAccess), [widgetAccess])

  useEffect(() => {
    // wait until we have something loaded before setting initial baselines
    const hasOptionsLoaded = safeSubscriptionSettings.length > 0

    if (snapshotKeyRef.current !== snapshotKey && hasOptionsLoaded) {
      snapshotKeyRef.current = snapshotKey

      savedBaselineRef.current.optionsStr = stableStringify(getCurrentOptions())
      savedBaselineRef.current.componentsStr = stableStringify(getCurrentComponents())

      if (supportsWidgets) {
        const defaultsArr = subscriptionSettingsData?.[mapping.defaultKey] || []
        const accessArr = subscriptionSettingsData?.[mapping.accessKey] || []

        savedBaselineRef.current.defaultWidgetsStr = stableStringify(Array.isArray(defaultsArr) ? defaultsArr : [])
        savedBaselineRef.current.widgetAccessStr = stableStringify(Array.isArray(accessArr) ? accessArr : [])
      } else {
        savedBaselineRef.current.defaultWidgetsStr = stableStringify([])
        savedBaselineRef.current.widgetAccessStr = stableStringify([])
      }
    }
  }, [
    snapshotKey,
    safeSubscriptionSettings,
    componentFeatures,
    tabTier,
    supportsWidgets,
    mapping,
    subscriptionSettingsData,
  ])

  const optionsDirty =
    savedBaselineRef.current.optionsStr !== '' && currentOptionsStr !== savedBaselineRef.current.optionsStr
  const componentsDirty =
    savedBaselineRef.current.componentsStr !== '' && currentComponentsStr !== savedBaselineRef.current.componentsStr
  const widgetsDirty = supportsWidgets
    ? (currentDefaultWidgetsStr !== savedBaselineRef.current.defaultWidgetsStr ||
        currentWidgetAccessStr !== savedBaselineRef.current.widgetAccessStr)
    : false

  const anyDirty = optionsDirty || componentsDirty || widgetsDirty

  /* ------------------------------------------------------------------ */
  /* Save handlers                                                      */
  /* ------------------------------------------------------------------ */

  const updatePermissions = () => {
    if (!anyDirty || isSend) return
    if (safeSubscriptionSettings.length === 0) return

    const currentTabData = safeSubscriptionSettings.find(obj => obj.idTab === optionTab)
    if (!currentTabData) return

    setIsSend(true)

    const payload = {
      features: currentTabData?.existingUsers?.optionsList,
      components: componentFeatures?.[tabTier],
      type: keyTabOptions?.[`${optionTab}`],
    }

    if (supportsWidgets) {
      payload.defaultWidgets = defaultWidgets
      payload.widgetAccess = widgetAccess
    }

    saveSubscriptionSettings(payload)
      .then(res => {
        // mark current state as saved (strings) so buttons disable again
        savedBaselineRef.current.optionsStr = currentOptionsStr
        savedBaselineRef.current.componentsStr = currentComponentsStr
        savedBaselineRef.current.defaultWidgetsStr = supportsWidgets ? currentDefaultWidgetsStr : stableStringify([])
        savedBaselineRef.current.widgetAccessStr = supportsWidgets ? currentWidgetAccessStr : stableStringify([])

        setIsSend(false)
        successAlert('Subscription settings have been updated')
      })
      .catch(error => {
        setIsSend(false)
        successAlert(error.message)
      })
  }

  const updateOrganizationsPermissions = () => {
    if (safeSubscriptionSettings.length === 0) return

    const currentTabData = safeSubscriptionSettings.find(obj => obj.idTab === optionTab)
    if (!currentTabData) return

    setIsSend(true)

    updateOrganizationsSettings({
      features: currentTabData?.existingUsers?.optionsList,
      type: keyTabOptions?.[`${optionTab}`],
    })
      .then(res => {
        setIsSend(false)
        successAlert('Subscription settings have been applied')
      })
      .catch(error => {
        setIsSend(false)
        successAlert(error.message)
      })
  }

  const canEdit = isEnabled(permissions, 'subscription-settings', '', 'edit')

  const UpdateChangesRow = ({ rowKey, section }) => {
    if (!canEdit) return null

    const sectionDirty =
      section === 'options'
        ? optionsDirty
        : section === 'components'
        ? componentsDirty
        : section === 'widgets'
        ? widgetsDirty
        : anyDirty

    const disabled = isSend || !sectionDirty

    return (
      <Grid container item justifyContent="flex-start" style={{ marginTop: 16 }} key={rowKey}>
        <Button
          variant="contained"
          color="success"
          pending={isSend}
          disabled={disabled}
          onClick={updatePermissions}
        >
          {trans('update-changes')}
        </Button>
      </Grid>
    )
  }

  return (
    <TabPanel
      value={optionTab}
      tab={optionTab}
      index={optionTab}
      style={{ marginBottom: '32px' }}
      key={`existing-users-tab-panel-${optionTab}`}
    >
      <Paper
        component={SpacingContainer}
        py={3}
        px="5sm3"
        key={`existing-users-tabpanel-paper-${optionTab}`}
      >
        <SpacingContainer>
          {isPageLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="320px"
              width="100%"
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Header row for visibility/access toggles */}
              <Grid
                key={`existing-users-tab-grid-${optionTab}`}
                container
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Grid
                  item
                  md={3}
                  xs={12}
                  key={`existing-users-tab-grid-title-${optionTab}`}
                >
                  <Typography
                    variant="h4"
                    weight="500"
                    fontFamily="lexend"
                    fontSize="20px"
                    style={{ alignItems: 'center', display: 'flex' }}
                    key={`existing-users-tab-grid-title-typography-${optionTab}`}
                  >
                    {'For All Users'}
                  </Typography>
                </Grid>

                <Grid
                  item
                  style={{ alignItems: 'center', display: 'flex' }}
                  key={`existing-users-tab-grid-visibility-title-${optionTab}`}
                >
                  <IconVLetter />
                  <Typography
                    weight="600"
                    key={`existing-users-typogpraphy-grid-visibility-title-${optionTab}`}
                    fontFamily="Manrope"
                    fontSize="14px"
                    style={{ alignItems: 'center', display: 'flex', marginLeft: '6px' }}
                  >
                    {`Visibility`}
                  </Typography>
                  <Button
                    color="primary"
                    variant="text"
                    disabled={safeSubscriptionSettings.length === 0}
                    onClick={() => selectUnselectAll(true, true)}
                  >
                    {trans('select-all')}
                  </Button>
                  <Button
                    color="primary"
                    variant="text"
                    disabled={safeSubscriptionSettings.length === 0}
                    onClick={() => selectUnselectAll(false, true)}
                  >
                    {trans('unselect-all')}
                  </Button>
                </Grid>

                <Grid item style={{ alignItems: 'center', display: 'flex' }}>
                  <IconALetter />
                  <Typography
                    weight="600"
                    fontFamily="Manrope"
                    fontSize="14px"
                    style={{ alignItems: 'center', display: 'flex', marginLeft: '6px' }}
                  >
                    {`Accessibility`}
                  </Typography>
                  <Button
                    color="primary"
                    variant="text"
                    disabled={safeSubscriptionSettings.length === 0}
                    onClick={() => selectUnselectAll(true, false)}
                  >
                    {trans('select-all')}
                  </Button>
                  <Button
                    color="primary"
                    variant="text"
                    disabled={safeSubscriptionSettings.length === 0}
                    onClick={() => selectUnselectAll(false, false)}
                  >
                    {trans('unselect-all')}
                  </Button>
                </Grid>
              </Grid>

              <Divider light spacing={2} />

              <OptionsForm newUser={false} />
              <UpdateChangesRow rowKey={`update-after-options-${optionTab}`} section="options" />

              <Divider variant="middle" width="inherit" light spacing={4} xs={12} />
              <ComponentFeaturesForm />
              <UpdateChangesRow rowKey={`update-after-components-${optionTab}`} section="components" />

              {/* Widgets section (only for tiers that support widgets) */}
              {supportsWidgets && (
                <>
                  <WidgetSettingsSection
                    defaultSelected={defaultWidgets}
                    accessSelected={widgetAccess}
                    onChangeDefault={setDefaultWidgets}
                    onChangeAccess={setWidgetAccess}
                  />
                  <UpdateChangesRow rowKey={`update-after-widgets-${optionTab}`} section="widgets" />
                </>
              )}
            </>
          )}
        </SpacingContainer>
      </Paper>
    </TabPanel>
  )
}

export default connect(ManageFeatures)
